import { useEffect, useMemo, useState } from 'react';
import { POLLING_INTERVAL, planStatusEnum } from 'common/utils/enums';
import PlansTable from 'components/plans/plans-table';
import Button from 'common/components/button';
import DashboardWrapperLayout from 'layouts/dashboard-wrapper';
import TablePaginateWrapper from 'common/layouts/table-paginate-wrapper';
import { useGetAllPlansQuery } from 'redux/api/ciosUsersApi/ciosUsersApi';
import { isEmpty } from 'common/utils/isEmpty';
import useUserTracker from 'common/utils/user-tracker/useUserTracker';
import useTracker from 'common/utils/user-tracker/useTracker';
import EmptyPlans from 'components/empty-pages/empty-plans';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ButtonGroup, Flex } from '@chakra-ui/react';
import PlanInfoCard from 'components/plans/plan-info-card';
import ContactCard from 'components/shared/contact-card';
import { PlanApplyButton } from 'components/plans/plan-apply-modal';
import EmptyPage from 'common/components/empty-page';
import { useSelector } from 'react-redux';
import { selectUser } from 'redux/features/auth/authSlice';
import PlanCreateButton from 'components/plans/plan-create-button';
import moment from 'moment';

const Plans = () => {
  // DATA INITIALIZATION
  const pageSize = 10;
  const navigate = useNavigate();

  // STATES
  const [currentPage, setCurrentPage] = useState(1);
  const [pollingInterval, setPollingInterval] = useState(
    () => POLLING_INTERVAL
  );

  const statuses = [
    planStatusEnum.CREATED,
    planStatusEnum.QUOTE,
    planStatusEnum.PENDING,
    planStatusEnum.ACTIVATION_REQUIRED,
    planStatusEnum.REVIEW,
    planStatusEnum.ACTIVE,
    planStatusEnum.ON_GOING_CLAIM,
  ]

  // HOOKS
  const { data: plansData, isLoading: getAllPlansLoading } =
    useGetAllPlansQuery({
      sort: '-createdAt',
      limit: pageSize,
      status: statuses,
    }, {
      pollingInterval,
      refetchOnMountOrArgChange: true,
    });
  const { trackLink } = useUserTracker();
  useTracker({ pageId: 'plansList' });
  const [minPageLimit, maxPageLimit] = useMemo(() => {
    const minPageLimit = (currentPage - 1) * pageSize;
    const maxPageLimit = currentPage * pageSize - 1;
    return [minPageLimit, maxPageLimit];
  }, [currentPage]);

  // Retrieve user details
  const userDetails = useSelector(selectUser);

  // USE EFFECT
  useEffect(() => {
    if (isEmpty(plansData)) {
      setPollingInterval(0);
    } else {
      setPollingInterval(POLLING_INTERVAL);
    }
  }, [plansData]);

  // Filter plansData for quotes and applications in progress.
  const quotePlans = useMemo(() => {
    const quoteStatuses = [
      planStatusEnum.CREATED,
      planStatusEnum.QUOTE,
      planStatusEnum.PENDING,
    ];
    if (isEmpty(plansData)) return [];
    return plansData.filter((plan) => {
      const isQuote = quoteStatuses.includes(plan.status);

      // Additional safety to make sure we don't show expired plans
      // A quote is expired if its expirationDate date is in the past.
      // We must compare in UTC, as the expirationDate is stored in UTC.
      // A date like '2025-01-01T02:00:00Z' would be '2024-12-31' in a US timezone,
      // causing it to seem expired a day early if we compare in local time.
      const isExpired =
        plan.expirationDate &&
        moment.utc(plan.expirationDate).isBefore(moment.utc(), 'day');

      return isQuote && !isExpired;
    });
  }, [plansData]);

  // Filter plansData down to only the active plans  
  const activePlans = useMemo(() => {
    const activeStatuses = [
      planStatusEnum.ACTIVE,
      planStatusEnum.ON_GOING_CLAIM,
    ]
    if (isEmpty(plansData)) return [];
    return plansData.filter(
      (plan) => activeStatuses.includes(plan.status)
    );
  }, [plansData]);

  // Filter plansData down to only the in-review plans
  
  const inReviewPlans = useMemo(() => {
    const reviewStatuses = [
      planStatusEnum.REVIEW,
      planStatusEnum.ACTIVATION_REQUIRED,
    ]
    if (isEmpty(plansData)) return [];
    return plansData.filter(
      (plan) => reviewStatuses.includes(plan.status)
    );
  }, [plansData]);

  // Set up tabs for page state
  // Set up url query params for activeTab
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTabFromUrl = searchParams.get('tab');

  const tabsConfig = useMemo(() => {
    const config = [];
    if (!isEmpty(activePlans)) {
      config.push({ id: 'active', title: 'Active Plans' });
    }
    if (!isEmpty(inReviewPlans)) {
      config.push({ id: 'activate', title: 'In Review' });
    }
    // Always show the quotes tab
    config.push({ id: 'quotes', title: 'Quotes' });
    return config;
  }, [activePlans, inReviewPlans]);

  const [activeTab, setActiveTab] = useState(() => {
    const defaultTab = tabsConfig.length > 0 ? tabsConfig[0].id : 'quotes';
    const requestedTab = tabsConfig.find((t) => t.id === activeTabFromUrl);
    return requestedTab ? requestedTab.id : defaultTab;
  });

  // keep URL in sync with active tab
  useEffect(() => {
    // merge only the 'tab' param, preserving others, avoid flicker on identical value
    if (searchParams.get('tab') !== activeTab) {
      const params = new URLSearchParams(searchParams);
      params.set('tab', activeTab);
      setSearchParams(params, { replace: true });
    }
  }, [activeTab, searchParams, setSearchParams]);

  // Set up tabs for active plans and quotes
  const tabs = useMemo(
    () =>
      tabsConfig.map((tab) => ({
        title: tab.title,
        selected: activeTab === tab.id,
        onClick: () => setActiveTab(tab.id),
      })),
    [activeTab, tabsConfig]
  );

  // Set up cta buttons for the header
  const buttons = (
    <ButtonGroup>
      {/* <PlanApplyButton /> */}
      <PlanCreateButton
        mode='purchase'
        title='Purchase Now'
        theme='secondary'
      />
      <PlanCreateButton mode='quote' title='Start Quote' theme='primary' />
    </ButtonGroup>
  );

  return (
    <DashboardWrapperLayout title='Plans' buttons={buttons} tabs={tabs}>
      <div className='flex flex-col w-full gap-12'>
        {getAllPlansLoading ? (
          <EmptyPlans isLoading />
        ) : isEmpty(plansData) ? (
          <EmptyPlans />
        ) : (
          <>
            {activeTab === 'active' && (
              <Flex className='flex-col gap-6'>
                {!isEmpty(activePlans) ? (
                  activePlans.map((plan) => (
                    <Flex
                      key={plan.id}
                      className='flex-col gap-4 w-full p-4 rounded-lg border border-gray-200'
                    >
                      <PlanInfoCard plan={plan} member={userDetails} />
                      <Flex className='justify-start gap-3 mobile:flex-col'>
                        <Button
                          className='cursor-pointer hover:opacity-80 transition-all'
                          onClick={() =>
                            navigate(`/dashboard/plans/${plan.id}`)
                          }
                          title='View Plan Details'
                          wFull={false}
                        />
                      </Flex>
                    </Flex>
                  ))
                ) : (
                  <EmptyPage
                    title='No Active Plans'
                    subtitle='You have no active plans at this time.'
                    FullButton={
                      <PlanCreateButton
                        mode='quote'
                        title='Start Quote'
                        theme='primary'
                      />
                    }
                  />
                )}
              </Flex>
            )}
            {activeTab === 'activate' && (
              <Flex className='flex-col gap-6'>
                {!isEmpty(inReviewPlans) ? (
                  <TablePaginateWrapper
                    pageSize={pageSize}
                    setCurrentPage={setCurrentPage}
                    currentPage={currentPage}
                    totalCount={inReviewPlans?.length}
                  >
                    <PlansTable
                      plans={inReviewPlans?.slice(minPageLimit, maxPageLimit)}
                    />
                  </TablePaginateWrapper>
                ) : (
                  <EmptyPage
                    title='No Plans In Review'
                    subtitle='You have no plans in review at this time.'
                  />
                )}
              </Flex>
            )}
            {activeTab === 'quotes' && (
              <Flex className='flex-col gap-6'>
                {!isEmpty(quotePlans) ? (
                  <TablePaginateWrapper
                    pageSize={pageSize}
                    setCurrentPage={setCurrentPage}
                    currentPage={currentPage}
                    totalCount={quotePlans?.length}
                  >
                    <PlansTable
                      plans={quotePlans?.slice(minPageLimit, maxPageLimit)}
                    />
                  </TablePaginateWrapper>
                ) : (
                  <EmptyPage
                    title='No Quotes or Applications'
                    subtitle='You have no quotes or applications at this time.'
                  />
                )}
              </Flex>
            )}
          </>
        )}

        {/* help */}
        <ContactCard title='Need Help With a Plan?'>
          Contact us by email, phone, or text and we can help you answer any
          questions you may have about your plan.
        </ContactCard>
      </div>
    </DashboardWrapperLayout>
  );
};

export default Plans;
